import { TeamSettings } from './TeamSettings';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useSync } from '../lib/sync';
import { usePlan } from '../lib/plan';
import { PlanSection } from './PlanSection';
import { Icon } from '../lib/icons';
import { shortDate } from '../lib/format';
import { IntegrationsSection } from './IntegrationsSection';
import { Picker, useToast } from './ui';
import { BusinessDialog, useBusinesses } from './dialogs';

const SETTINGS_TABS = [
  { id: 'team', label: 'Team', admin: true },
  { id: 'billing', label: 'Tarif & Nutzung', admin: false },
  { id: 'ai', label: 'KI', admin: false },
  { id: 'integrations', label: 'Integrationen', admin: false },
  { id: 'branding', label: 'Branding', admin: true },
] as const;
type SettingsTab = typeof SETTINGS_TABS[number]['id'];

type Provider = 'openai' | 'anthropic' | 'gemini';

interface AiState {
  keys: { provider: Provider; hint: string; verifiedAt: string | null }[];
  settings: {
    transcribeProvider: 'openai' | 'gemini' | 'lokal' | null;
    transcribeModel: string | null;
    analyzeProvider: Provider | null;
    analyzeModel: string | null;
    promptExtra: string | null;
  };
  tenantPromptExtra: string | null;
  defaultPrompt: string;
  defaultModels: Record<Provider, { transcribe: string | null; analyze: string }>;
  localTranscribe?: boolean;
  usage: { provider: Provider; model: string; kind: string; runs: number; ok: number; inputTokens: number; outputTokens: number; lastAt: string; lastError: string | null }[];
}

const KIND_LABEL: Record<string, string> = { transkription: 'Transkription', auswertung: 'Auswertung', zusammenfassung: 'Zusammenfassung' };

const PROVIDERS: { id: Provider; name: string; note: string }[] = [
  { id: 'openai', name: 'OpenAI', note: 'Transkription und Auswertung' },
  { id: 'gemini', name: 'Google Gemini', note: 'Transkription und Auswertung' },
  { id: 'anthropic', name: 'Anthropic Claude', note: 'Nur Auswertung, keine Transkription' },
];

export function SettingsPage({ onClose, initialTab = 'team', navigationRequest = 0 }: { onClose: () => void; initialTab?: 'team' | 'billing'; navigationRequest?: number }) {
  const { me } = useSync();
  const [tab, setTab] = useState<SettingsTab>(me.role === 'mitglied' ? 'billing' : initialTab);
  const content = useRef<HTMLDivElement>(null);
  const tabs = SETTINGS_TABS.filter(t => !t.admin || me.role !== 'mitglied');
  const selectTab = (id: SettingsTab) => { setTab(id); content.current?.scrollTo({ top: 0 }); };
  useEffect(() => { setTab(me.role === 'mitglied' ? 'billing' : initialTab); content.current?.scrollTo({ top: 0 }); }, [initialTab, me.role, navigationRequest]);
  const plan = usePlan();
  const toast = useToast();
  const businesses = useBusinesses();
  const [businessId, setBusinessId] = useState<string | null>(businesses[0]?.id ?? null);
  const [state, setState] = useState<AiState | null>(null);
  const [drafts, setDrafts] = useState<Partial<Record<Provider, string>>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [addBusiness, setAddBusiness] = useState(false);
  const [connectCode, setConnectCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [connections, setConnections] = useState<{ mcpUrl: string; connections: { id: string; name: string | null; createdAt: string; lastUsedAt: string | null }[] } | null>(null);

  const loadConnections = useCallback(async () => {
    try {
      setConnections(await api('/connections'));
    } catch {
      // Abschnitt bleibt dann leer
    }
  }, []);

  useEffect(() => {
    void loadConnections();
  }, [loadConnections]);
  const isAdmin = me.role !== 'mitglied';

  const load = useCallback(async () => {
    if (!businessId) return;
    try {
      setState(await api<AiState>(`/settings/ai/${businessId}`));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Einstellungen konnten nicht geladen werden');
    }
  }, [businessId, toast]);

  useEffect(() => {
    setState(null);
    void load();
  }, [load]);

  async function saveKey(p: Provider) {
    const key = drafts[p]?.trim();
    if (!key || !plan.allowFeature('ki')) return;
    setBusy(p);
    try {
      await api(`/settings/ai/${businessId}/keys/${p}`, { method: 'PUT', json: { key } });
      setDrafts((d) => ({ ...d, [p]: '' }));
      toast('Key geprüft und gespeichert');
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  }

  async function removeKey(p: Provider) {
    setBusy(p);
    try {
      await api(`/settings/ai/${businessId}/keys/${p}`, { method: 'DELETE' });
      toast('Key entfernt');
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function testKey(p: Provider) {
    setBusy(p);
    try {
      const r = await api<{ ok: boolean; error?: string }>(`/settings/ai/${businessId}/keys/${p}/test`, { method: 'POST' });
      toast(r.ok ? 'Key funktioniert' : `Key funktioniert nicht: ${r.error}`);
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function saveSettings(patch: Partial<AiState['settings']>, tenantPrompt?: string | null) {
    if (!state) return;
    const settings = { ...state.settings, ...patch };
    setState({ ...state, settings, tenantPromptExtra: tenantPrompt !== undefined ? tenantPrompt : state.tenantPromptExtra });
    try {
      await api(`/settings/ai/${businessId}`, { method: 'PUT', json: settings });
      if (tenantPrompt !== undefined) await api('/settings/prompt', { method: 'PUT', json: { promptExtra: tenantPrompt } });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  async function backfill() {
    setBusy('backfill');
    try {
      const r = await api<{ queued: number }>(`/settings/ai/${businessId}/backfill`, { method: 'POST' });
      toast(r.queued ? `${r.queued} Medien werden ausgewertet` : 'Keine Medien ohne Auswertung gefunden');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Nachauswertung fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  }

  const has = (p: Provider) => state?.keys.some((k) => k.provider === p) ?? false;
  const canTranscribe = has('openai') || has('gemini');
  const business = businesses.find((b) => b.id === businessId);

  return (
    <>
      <header className="chead">
        <button className="iconbtn plain min-[821px]:hidden" onClick={onClose} aria-label="Zurück zur Liste"><Icon name="back" /></button>
        <div className="min-w-0 flex-1">
          <b className="block truncate text-[15.5px]">Einstellungen</b>
          <span className="text-xs text-[var(--muted)]">Dein Team, eure Werkzeuge und euer Tarif</span>
        </div>
        <button className="iconbtn plain" onClick={onClose} aria-label="Einstellungen schließen" data-tip="Schließen" data-tip-below=""><Icon name="x" size={20} /></button>
      </header>
      <div className="settings-tabs" role="tablist" aria-label="Einstellungsbereiche">
        {tabs.map((item, index) => <button key={item.id} id={`settings-tab-${item.id}`} role="tab" aria-selected={tab === item.id} aria-controls={`settings-panel-${item.id}`} tabIndex={tab === item.id ? 0 : -1} onClick={() => selectTab(item.id)} onKeyDown={e => {
          const next = e.key === 'ArrowRight' ? (index + 1) % tabs.length : e.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null;
          if (next === null) return;
          e.preventDefault(); selectTab(tabs[next].id); document.getElementById(`settings-tab-${tabs[next].id}`)?.focus();
        }}>{item.label}</button>)}
      </div>
      <div ref={content} className="scroll flex-1 px-5 py-5 max-[560px]:px-4">
        {isAdmin && <div className="settings-panel" id="settings-panel-team" role="tabpanel" aria-labelledby="settings-tab-team" hidden={tab !== 'team'}><TeamSettings section="team" />
        {isAdmin && (
          <section className="border-t border-[var(--line)] pt-4">
            <h3 className="m-0 mb-1 text-[14px] font-semibold">Geschäfte</h3>
            <p className="m-0 mb-3 text-[13.5px] text-[var(--muted)]">
              {businesses.length > 1
                ? `${businesses.length} Geschäfte mit getrennten Projekten und KI-Keys.`
                : 'Führst du mehrere getrennte Geschäfte? Dann bekommt jedes eigene Projekte und KI-Keys.'}
            </p>
            <button className="btn btn-ghost" onClick={() => plan.more('businesses') && setAddBusiness(true)}>
              <Icon name="plus" size={18} />Weiteres Geschäft anlegen
              {plan.info && plan.info.limits.businesses !== null && plan.info.usage.businesses >= plan.info.limits.businesses && <span className="plus-tag">Business</span>}
            </button>
          </section>
        )}
        </div>}
        <div className="settings-panel" id="settings-panel-billing" role="tabpanel" aria-labelledby="settings-tab-billing" hidden={tab !== 'billing'}><PlanSection />{isAdmin && <TeamSettings section="billing" />}</div>
        {isAdmin && <div className="settings-panel" id="settings-panel-branding" role="tabpanel" aria-labelledby="settings-tab-branding" hidden={tab !== 'branding'}><TeamSettings section="branding" /></div>}
        <div className="settings-panel" id="settings-panel-ai" role="tabpanel" aria-labelledby="settings-tab-ai" hidden={tab !== 'ai'}>
        <p className="m-0 text-[14px] text-[var(--muted)]">
          Mit einem eigenen API-Key werden Sprachnachrichten und Videos in Text umgewandelt, zusammengefasst und durchsuchbar. Die Kosten rechnet der Anbieter direkt mit euch ab.
        </p>
        {plan.info && !plan.info.features.includes('ki') && (
          <div className="upgrade-reason">
            <Icon name="lock" size={18} />
            <span>
              KI mit eigenem Schlüssel gibt es ab <b>Plus</b>. Gespeicherte Schlüssel bleiben erhalten und werden wieder genutzt, sobald der Tarif sie enthält.{' '}
              <button className="font-semibold text-[var(--brand-a)] underline" onClick={() => plan.showUpgrade()}>Tarife ansehen</button>
            </span>
          </div>
        )}

        {businesses.length > 1 && (
          <Picker label="Geschäft" value={businessId} onChange={setBusinessId} options={businesses.map((b) => ({ value: b.id, label: b.name, hint: 'Eigene Keys und Einstellungen' }))} />
        )}

        {!isAdmin && <div className="check">Nur Admins können Keys und Einstellungen ändern.</div>}

        {!state ? (
          <div className="grid place-items-center py-6"><div className="spinner" /></div>
        ) : (
          <>
            <section className="flex flex-col gap-3">
              <h3 className="m-0 text-[14px] font-semibold">API-Keys{businesses.length > 1 ? ` für ${business?.name}` : ''}</h3>
              {PROVIDERS.map((p) => {
                const key = state.keys.find((k) => k.provider === p.id);
                return (
                  <div key={p.id} className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-3">
                    <div className="flex items-center gap-2">
                      <Icon name="key" size={18} className={key ? 'text-[var(--loesung)]' : 'text-[var(--dim)]'} />
                      <span className="flex-1">
                        <b className="block text-[14px]">{p.name}</b>
                        <small className="text-xs text-[var(--muted)]">
                          {key ? `Hinterlegt ${key.hint}${key.verifiedAt ? `, geprüft am ${shortDate(key.verifiedAt)}` : ''}` : p.note}
                        </small>
                      </span>
                      {key && isAdmin && (
                        <>
                          <button className="chip" disabled={busy === p.id} onClick={() => void testKey(p.id)}>Testen</button>
                          <button className="iconbtn plain" disabled={busy === p.id} onClick={() => void removeKey(p.id)} aria-label={`${p.name}-Key entfernen`}><Icon name="trash" size={17} /></button>
                        </>
                      )}
                    </div>
                    {isAdmin && (
                      <form className="mt-2 flex gap-2" onSubmit={(e) => (e.preventDefault(), void saveKey(p.id))}>
                        <input
                          className="input h-10 min-w-0 flex-1"
                          type="password"
                          autoComplete="off"
                          placeholder={key ? 'Neuen Key eingeben, um ihn zu ersetzen' : 'API-Key einfügen'}
                          value={drafts[p.id] ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                          aria-label={`${p.name}-Key`}
                        />
                        <button className="btn btn-primary h-10" disabled={!drafts[p.id]?.trim() || busy === p.id}>{busy === p.id ? 'Prüfe …' : 'Speichern'}</button>
                      </form>
                    )}
                  </div>
                );
              })}
              {state.localTranscribe ? (
                <div className="check ok">
                  <Icon name="mic" size={17} />
                  Transkription läuft über den eigenen Dienst auf diesem Server, dafür ist kein Key nötig. Für Kurzfassungen und Inhaltspunkte braucht es weiterhin einen Anbieter.
                </div>
              ) : state.keys.length > 0 && !canTranscribe ? (
                <div className="check">Für die Transkription braucht es zusätzlich einen Key von OpenAI oder Gemini.</div>
              ) : null}
            </section>

            {isAdmin && (state.keys.length > 0 || state.localTranscribe) && (
              <section className="flex flex-col gap-3">
                <h3 className="m-0 text-[14px] font-semibold">Verwendung</h3>
                <Picker
                  label="Transkription mit"
                  value={state.settings.transcribeProvider}
                  onChange={(v) => void saveSettings({ transcribeProvider: v, transcribeModel: null })}
                  options={[
                    ...(state.localTranscribe ? [{ value: 'lokal' as const, label: 'Eigener Dienst (ohne Key)', hint: 'Läuft auf diesem Server, keine Kosten beim Anbieter' }] : []),
                    ...(['openai', 'gemini'] as const).filter(has).map((p) => ({ value: p, label: PROVIDERS.find((x) => x.id === p)!.name })),
                  ]}
                  placeholder="Anbieter wählen"
                />
                <Picker
                  label="Auswertung mit"
                  value={state.settings.analyzeProvider}
                  onChange={(v) => void saveSettings({ analyzeProvider: v, analyzeModel: null })}
                  options={PROVIDERS.filter((p) => has(p.id)).map((p) => ({ value: p.id, label: p.name }))}
                  placeholder="Anbieter wählen"
                />
                <div className="grid grid-cols-2 gap-3 max-[520px]:grid-cols-1">
                  {state.settings.transcribeProvider && state.settings.transcribeProvider !== 'lokal' && has(state.settings.transcribeProvider) && (
                    <ModelPicker
                      label="Modell Transkription"
                      businessId={businessId!}
                      provider={state.settings.transcribeProvider}
                      value={state.settings.transcribeModel}
                      fallback={state.defaultModels[state.settings.transcribeProvider].transcribe ?? ''}
                      filter={(m) => (state.settings.transcribeProvider === 'openai' ? /whisper|transcribe/.test(m) : true)}
                      onChange={(v) => void saveSettings({ transcribeModel: v })}
                    />
                  )}
                  {state.settings.analyzeProvider && has(state.settings.analyzeProvider) && (
                    <ModelPicker
                      label="Modell Auswertung"
                      businessId={businessId!}
                      provider={state.settings.analyzeProvider}
                      value={state.settings.analyzeModel}
                      fallback={state.defaultModels[state.settings.analyzeProvider].analyze}
                      filter={(m) => !/whisper|transcribe|embedding/.test(m)}
                      onChange={(v) => void saveSettings({ analyzeModel: v })}
                    />
                  )}
                </div>
                {!state.settings.transcribeProvider && !state.settings.analyzeProvider && (
                  <p className="m-0 text-[13px] text-[var(--muted)]">Ohne Auswahl nutzt Mach's automatisch den ersten passenden Key.</p>
                )}
                <label className="field">
                  Hinweise für {businesses.length > 1 ? business?.name : 'die Auswertung'}
                  <textarea
                    className="input min-h-[84px] py-3"
                    defaultValue={state.settings.promptExtra ?? ''}
                    onBlur={(e) => void saveSettings({ promptExtra: e.target.value.trim() || null })}
                    placeholder="z. B. Wir sind eine Webagentur. Nenne betroffene Seiten und Browser."
                    maxLength={4000}
                  />
                </label>
                {businesses.length > 1 && (
                  <label className="field">
                    Hinweise für alle Geschäfte
                    <textarea
                      className="input min-h-[70px] py-3"
                      defaultValue={state.tenantPromptExtra ?? ''}
                      onBlur={(e) => void saveSettings({}, e.target.value.trim() || null)}
                      maxLength={4000}
                    />
                  </label>
                )}
              </section>
            )}

            {state.keys.length > 0 && (
              <section className="flex flex-col gap-2">
                <h3 className="m-0 text-[14px] font-semibold">Nutzung der letzten 30 Tage</h3>
                {state.usage.length === 0 ? (
                  <p className="m-0 text-[13.5px] text-[var(--muted)]">Noch keine Auswertungen. Neue Sprachnachrichten, Videos und Bilder werden ab jetzt automatisch ausgewertet.</p>
                ) : (
                  <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                    {state.usage.map((u) => (
                      <li key={`${u.provider}-${u.model}-${u.kind}`} className="flex items-baseline gap-2 text-[13.5px]">
                        <span className="flex-1 truncate">{KIND_LABEL[u.kind] ?? u.kind} <span className="text-[var(--muted)]">mit {u.model}</span></span>
                        <span className="text-[var(--muted)]">{u.runs}×{u.ok < u.runs ? `, ${u.runs - u.ok} fehlgeschlagen` : ''}</span>
                        <span className="w-[110px] text-right text-[var(--muted)]">{(u.inputTokens + u.outputTokens).toLocaleString('de-DE')} Tokens</span>
                      </li>
                    ))}
                  </ul>
                )}
                {state.usage.find((u) => u.lastError) && (
                  <div className="check">Letzter Fehler: {state.usage.find((u) => u.lastError)!.lastError}</div>
                )}
                {isAdmin && (
                  <button className="btn btn-ghost self-start" disabled={busy === 'backfill'} onClick={() => void backfill()}>
                    <Icon name="sparkle" size={17} />Ältere Medien der letzten 30 Tage auswerten
                  </button>
                )}
              </section>
            )}
          </>
        )}

        </div>
        <div className="settings-panel" id="settings-panel-integrations" role="tabpanel" aria-labelledby="settings-tab-integrations" hidden={tab !== 'integrations'}>
        <section className="border-t border-[var(--line)] pt-4">
          <h3 className="m-0 mb-1 text-[14px] font-semibold">
            KI-Assistenten verbinden
            {plan.info && !plan.info.features.includes('mcp') && <span className="plus-tag">Plus</span>}
          </h3>
          <p className="m-0 mb-3 text-[13.5px] text-[var(--muted)]">
            Arbeite mit Claude, ChatGPT oder einem anderen KI-Programm direkt in deinen Aufgaben: lesen, anlegen, Lösungen melden. Füge dort diese Adresse als MCP-Server bzw. Connector hinzu und melde dich mit deinem Mach's-Konto an.
          </p>
          {connections && (
            <>
              <div className="flex gap-2">
                <input className="input h-10 min-w-0 flex-1 font-mono text-[13px]" readOnly value={connections.mcpUrl} aria-label="MCP-Adresse" onFocus={(e) => e.currentTarget.select()} />
                <button className="btn btn-ghost h-10" onClick={() => void navigator.clipboard?.writeText(connections.mcpUrl).then(() => toast('Adresse kopiert'))}>Kopieren</button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button className="btn btn-ghost h-10" onClick={() => plan.allowFeature('mcp') && void api<{ code: string; expiresAt: string }>('/connections/code', { method: 'POST' }).then(setConnectCode)}>
                  <Icon name="key" size={17} />Verbindungscode erzeugen
                </button>
                {connectCode && (
                  <span className="text-[13.5px]">
                    <b className="font-mono text-[16px] tracking-wider">{connectCode.code}</b>
                    <span className="text-[var(--muted)]"> gilt bis {new Date(connectCode.expiresAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</span>
                  </span>
                )}
              </div>
              <p className="m-0 mt-1 text-[12.5px] text-[var(--muted)]">Für die Anmeldung im KI-Programm, wenn du dich sonst mit Apple anmeldest oder kein Passwort hast.</p>
              {connections.connections.length > 0 && (
                <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
                  {connections.connections.map((k) => (
                    <li key={k.id} className="flex items-center gap-2 rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2">
                      <Icon name="sparkle" size={18} className="text-[var(--brand-a)]" />
                      <span className="flex-1">
                        <b className="block text-[14px]">{k.name ?? 'KI-Client'}</b>
                        <small className="text-xs text-[var(--muted)]">Verbunden am {shortDate(k.createdAt)}{k.lastUsedAt ? `, zuletzt genutzt am ${shortDate(k.lastUsedAt)}` : ''}</small>
                      </span>
                      <button className="chip" onClick={async () => {
                        await api(`/connections/${k.id}`, { method: 'DELETE' });
                        toast('Verbindung getrennt');
                        void loadConnections();
                      }}>Trennen</button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        {isAdmin && <IntegrationsSection mcpUrl={connections?.mcpUrl ?? null} />}

        </div>
      {addBusiness && <BusinessDialog onClose={() => setAddBusiness(false)} onSaved={setBusinessId} />}
      </div>
    </>
  );
}

/** Modellauswahl; die Liste kommt direkt vom Anbieter, leer = Standardmodell. */
function ModelPicker({ label, businessId, provider, value, fallback, filter, onChange }: {
  label: string;
  businessId: string;
  provider: Provider;
  value: string | null;
  fallback: string;
  filter: (model: string) => boolean;
  onChange: (value: string | null) => void;
}) {
  const [models, setModels] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    setModels(null);
    api<{ models: string[] }>(`/settings/ai/${businessId}/models/${provider}`)
      .then((r) => !cancelled && setModels(r.models))
      .catch(() => !cancelled && setModels([]));
    return () => {
      cancelled = true;
    };
  }, [businessId, provider]);
  const list = (models ?? []).filter(filter);
  const options = [
    { value: '', label: `Standard (${fallback})` },
    ...list.filter((m) => m !== fallback).map((m) => ({ value: m, label: m })),
    ...(value && !list.includes(value) ? [{ value, label: value }] : []),
  ];
  return <Picker label={label} value={value ?? ''} onChange={(v) => onChange(v || null)} options={options} placeholder={models === null ? 'Lade Modelle …' : 'Standard'} />;
}
